import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

const QUERY_KEY = 'shared-flyer-a-title-image';

type UsePetitionTitleImageArgs = {
	title: string;
	/** For the error report only. */
	petitionId: string;
	/** The Sentry `feature` tag of the error report. */
	feature: string;
};

/**
 * Renders the petition title as a PNG data URL for the Flyer A PDF. The renderers chunk (the title's
 * embedded fonts and the PDF library) loads when this query starts, at mount. The image is kept for
 * the app session, so a remounted consumer does not render it again.
 */
export function usePetitionTitleImage({
	title,
	petitionId,
	feature,
}: UsePetitionTitleImageArgs): UseQueryResult<string> {
	const { errorReporter } = useUtilityContext();

	// eslint-disable-next-line @tanstack/query/exhaustive-deps -- `petitionId` and `feature` only tag the report
	return useQuery({
		queryKey: [QUERY_KEY, title],
		queryFn: async () => {
			try {
				const { renderPetitionTitleImage } = await import(/* webpackChunkName: "cmp/flyer_a" */ '../renderers');
				return await renderPetitionTitleImage(title);
			} catch (error) {
				void errorReporter.report({
					error: error instanceof Error ? error : new Error('Failed to renderPetitionTitleImage', { cause: error }),
					params: { event: 'renderPetitionTitleImage', petitionId },
					context: { feature },
				});
				throw error;
			}
		},
		staleTime: 'static',
		gcTime: Infinity,
		retry: false,
	});
}
